import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getFAQs } from '@/lib/api';
import SectionHeader from '@/components/SectionHeader';
import type { FAQItem } from '@/types';

const fallbackFAQs: FAQItem[] = [
  {
    id: '01',
    question: 'How do I place an order?',
    answer:
      'Four steps. Researcher account, cart, WhatsApp confirmation, dispatch. The site lists every compound with its current third-party certificate. Payment finalises on WhatsApp because card processors block research-chemical merchants in most countries.',
    bullets: [
      'Account creation requires age and research-use affirmation. Recorded with timestamp and IP.',
      'Payment is finalised in chat: bank transfer, card link, or crypto, depending on destination country.',
      'First WhatsApp reply typically lands within 30 minutes during UAE office hours.',
    ],
  },
  {
    id: '02',
    question: 'How long does shipping take?',
    answer:
      'Dispatched the next UAE business day. The table below covers prep, transit on DHL Express or FedEx International Priority, and a customs buffer.',
    bullets: [
      'Tracking link is emailed within 24 hours of dispatch.',
      'Cold chain held at 2–8 °C door to door: insulated box, gel pack and indicator card.',
    ],
  },
  {
    id: '03',
    question: 'Do you ship to my country?',
    answer:
      'Yes, worldwide. The table below lists verified destinations and the courier on each lane. If your country is not listed, message us on WhatsApp and we will quote a route.',
    bullets: [
      'All shipments use the international harmonised code for research chemicals (UN 3316). Customs paperwork is accurate, never falsified.',
      "Import duties and local taxes are the importer's responsibility. We do not collect them at checkout.",
    ],
  },
  {
    id: '04',
    question: 'How much is shipping?',
    answer:
      'Free. Every order. No minimum. The card below details what travels in the box on every shipment.',
    bullets: [
      'Insulated box, gel pack and tamper-evident seal: included.',
      'Carrier is DHL Express or FedEx International Priority depending on the lane.',
      'Local import duties or taxes, where applicable, are charged by the carrier on delivery.',
    ],
  },
  {
    id: '05',
    question: 'Can I track my order?',
    answer:
      'Yes. The DHL or FedEx tracking link is emailed within 24 hours of dispatch. The same link is mirrored inside your AURA account.',
    bullets: [
      'Visible states: collected, in transit, customs, out for delivery, delivered.',
      'If a status freezes for more than 24 hours we open a carrier trace on your behalf.',
    ],
  },
  {
    id: '06',
    question: 'What if my vial arrives damaged or warm?',
    answer:
      'Same-day replacement. Send a photo on WhatsApp showing the box and the cold-chain indicator card. We confirm within an hour and dispatch the replacement the same business day.',
    bullets: [
      'Counts as damaged: broken seal, visible breakage or red cold-chain indicator card.',
      'Counts as warm: gel pack fully thawed on arrival.',
      'No forms. No physical return of the original required.',
    ],
  },
  {
    id: '07',
    question: 'What is your refund policy?',
    answer:
      'Three scenarios, three resolutions. All are settled on WhatsApp within one business day. When both options apply, you choose between same-day reship and full refund.',
    bullets: [
      'Refunds return to the original payment method within 5 to 7 business days, depending on the rail.',
      'Chargebacks are slow for both sides. Always open a WhatsApp ticket first.',
    ],
  },
  {
    id: '08',
    question: 'How do I contact customer support?',
    answer:
      'WhatsApp is fastest. Email is the second channel. Both are staffed by the AURA team in Dubai. No bots, no outsourced ticket queue.',
    bullets: [
      'We reply in EN, ES, FR, DE and NL.',
      'Lab and chemistry questions are looped to our AURA lab team directly when needed.',
    ],
  },
];

function parseFAQItems(raw: FAQItem[] | undefined): FAQItem[] {
  if (!raw || raw.length === 0) return fallbackFAQs;
  return raw.map((item, index) => ({
    ...item,
    id: item.id || String(index + 1).padStart(2, '0'),
  }));
}

interface AccordionItemProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ item, isOpen, onToggle }: AccordionItemProps) {
  return (
    <div className="border-b border-rp-line bg-rp-paper">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left md:gap-4 md:px-5 md:py-3.5"
        aria-expanded={isOpen}
      >
        <span className="w-9 shrink-0 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-rp-forest">
          {item.id}
        </span>
        <h3 className="flex-1 text-sm font-semibold leading-snug tracking-tight text-rp-ink">
          {item.question}
        </h3>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center text-rp-forest"
          aria-hidden="true"
        >
          <Plus size={18} strokeWidth={1.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 gap-3 px-4 pb-5 md:grid-cols-[36px_1fr_18px] md:gap-4 md:px-5 md:pb-6">
              <span className="hidden md:block" aria-hidden="true" />
              <div className="max-w-[720px]">
                <p className="text-sm leading-relaxed text-rp-moss">{item.answer}</p>
                {item.bullets && item.bullets.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {item.bullets.map((bullet, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm leading-relaxed text-rp-moss"
                      >
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-rp-leaf" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <span className="hidden md:block" aria-hidden="true" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQPage() {
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: rawFaqs, isLoading } = useQuery<FAQItem[]>({
    queryKey: ['faqs'],
    queryFn: getFAQs,
    staleTime: 1000 * 60 * 5,
    initialData: fallbackFAQs,
  });

  const faqs = parseFAQItems(rawFaqs);

  const handleToggle = (id: string) => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <section className="border-b border-rp-line bg-rp-bone py-10 md:py-20">
      <div className="rp-container">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <SectionHeader
            eyebrow="FAQ"
            title="Common questions"
            description="The questions the AURA team gets most from procurement teams and principal investigators. Lab-side questions get looped to our AURA lab team directly. If yours isn't here, replies land within 6 business hours."
            align="center"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          className="mx-auto max-w-[760px] border-t border-rp-line bg-rp-paper"
        >
          {isLoading ? (
            <div className="divide-y divide-rp-line">
              {fallbackFAQs.map((item) => (
                <div key={item.id} className="px-4 py-3 md:px-5 md:py-3.5">
                  <div className="h-4 w-3/4 animate-pulse rounded bg-rp-bone-2" />
                </div>
              ))}
            </div>
          ) : (
            faqs.map((item) => (
              <AccordionItem
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onToggle={() => handleToggle(item.id)}
              />
            ))
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
          className="mx-auto mt-10 flex max-w-[760px] flex-col items-center justify-center gap-3 text-center sm:flex-row sm:gap-4"
        >
          <span className="text-sm text-rp-moss">Still have a question?</span>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-rp-forest px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em] text-rp-paper transition-colors hover:bg-rp-forest-2"
          >
            <MessageCircle size={16} />
            Contact the AURA team
            <ArrowRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
